import type { CalendarSlot } from './calendar.model';
import { expandRecurrence } from './recurrence.utils';
import { isoWeekdayFromDateKey, timeToMinutes } from './time.utils';

/**
 * A single location entry in a weekly pattern or per-day override.
 *
 * Both startTime and endTime are required — every location entry
 * covers an explicit time range [startTime, endTime).
 */
export interface LocationEntry {
  locationId: string;
  /** HH:mm (24-hour) */
  startTime: string;
  /** HH:mm (24-hour) — must be after startTime */
  endTime: string;
}

export interface PersonLocationAssignment {
  locationId: string;
  type: 'primary' | 'secondary' | 'home_office' | string;
}

const DEFAULT_WORK_LOCATION_MORNING_START = '08:00';
const DEFAULT_WORK_LOCATION_MORNING_END = '12:00';
const DEFAULT_WORK_LOCATION_AFTERNOON_START = '13:00';
const DEFAULT_WORK_LOCATION_AFTERNOON_END = '17:00';
const DEFAULT_WORK_LOCATION_WEEKDAYS: IsoWeekday[] = [0, 1, 2, 3, 4];

/**
 * Resolve the effective start/end in minutes for an entry.
 */
function resolveEntryMinutes(entry: LocationEntry): { start: number; end: number } | null {
  const start = timeToMinutes(entry.startTime);
  if (start === null) return null;

  const end = timeToMinutes(entry.endTime);
  if (end === null) return null;

  return { start, end };
}

/**
 * Weekday index used by work-location patterns: 0 = Monday … 6 = Sunday.
 *
 * NOTE: This is NOT actual ISO 8601 weekday numbering (which is 1=Mon..7=Sun). The
 * type name is historical. Use `isoWeekdayFromDateKey()` from `time.utils` to get
 * a value compatible with this type; never construct it from `Date.getDay()` directly.
 */
export type IsoWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Weekly location pattern stored in calendar.data.weeklyLocationPattern.
 * Each key is an ISO weekday (0–6). A missing key or empty array means the
 * employee has no declared location for that day (not bookable).
 */
export type WeeklyLocationPattern = Partial<Record<IsoWeekday, LocationEntry[]>>;

function hasWeeklyLocationPatternEntries(pattern: WeeklyLocationPattern | undefined): pattern is WeeklyLocationPattern {
  return !!pattern && Object.keys(pattern).length > 0;
}

function getPrimaryPersonLocation(personLocations: readonly PersonLocationAssignment[]): PersonLocationAssignment | null {
  return personLocations.find(location => location.type === 'primary') ?? null;
}

export function buildDefaultWeeklyLocationPattern(personLocations: readonly PersonLocationAssignment[]): WeeklyLocationPattern | undefined {
  const primaryLocation = getPrimaryPersonLocation(personLocations);
  if (!primaryLocation) {
    return undefined;
  }

  return Object.fromEntries(
    DEFAULT_WORK_LOCATION_WEEKDAYS.map(weekday => [
      weekday,
      [
        {
          locationId: primaryLocation.locationId,
          startTime: DEFAULT_WORK_LOCATION_MORNING_START,
          endTime: DEFAULT_WORK_LOCATION_MORNING_END,
        },
        {
          locationId: primaryLocation.locationId,
          startTime: DEFAULT_WORK_LOCATION_AFTERNOON_START,
          endTime: DEFAULT_WORK_LOCATION_AFTERNOON_END,
        },
      ],
    ])
  ) as WeeklyLocationPattern;
}

/** @deprecated Use recurring work-location slots with resolveEffectiveLocation() instead. */
export function resolveWeeklyLocationPattern(
  calendarData: unknown,
  personLocations: readonly PersonLocationAssignment[] = []
): WeeklyLocationPattern | undefined {
  const data = calendarData as Record<string, unknown> | null | undefined;
  const pattern = data?.['weeklyLocationPattern'] as WeeklyLocationPattern | undefined;

  if (hasWeeklyLocationPatternEntries(pattern)) {
    return pattern;
  }

  return buildDefaultWeeklyLocationPattern(personLocations);
}

/**
 * Shape of slot.data for a location-override calendar slot.
 * The slot itself is always isAllDay: true; time ranges live inside entries.
 */
export interface LocationOverrideSlotData {
  type: 'location-override';
  entries: LocationEntry[];
  text: string;
  icon: string;
}

/**
 * Type guard for LocationOverrideSlotData.
 */
export function isLocationOverrideSlotData(data: unknown): data is LocationOverrideSlotData {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return (data as Record<string, unknown>)['type'] === 'location-override';
}

/**
 * Shape of slot.data for a recurring work-location slot.
 */
export interface WorkLocationSlotData {
  type: 'work-location';
  entries: LocationEntry[];
  text: string;
  icon: string;
  locationMeta?: unknown;
}

/**
 * Type guard for WorkLocationSlotData.
 */
export function isWorkLocationSlotData(data: unknown): data is WorkLocationSlotData {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return (data as Record<string, unknown>)['type'] === 'work-location';
}

/**
 * Find the entry covering the requested time within a list of location entries.
 *
 * Every entry has an explicit [startTime, endTime) range.
 * When no timeStr is provided, returns the first entry (if any).
 *
 * Returns the first matching entry, or null.
 */
export function findMatchingEntry(entries: LocationEntry[], timeStr?: string): LocationEntry | null {
  if (entries.length === 0) return null;
  if (!timeStr) return entries[0];

  const requestMinutes = timeToMinutes(timeStr);
  if (requestMinutes === null) return null;

  for (const entry of entries) {
    const range = resolveEntryMinutes(entry);
    if (!range) continue;

    if (requestMinutes >= range.start && requestMinutes < range.end) {
      return entry;
    }
  }

  return null;
}

/**
 * Check whether a calendar slot covers a specific date.
 */
function slotCoversDate(slot: CalendarSlot, dateStr: string): boolean {
  if (slot.dateFrom === dateStr) return true;
  if (!slot.dateTo) return false;
  return slot.dateFrom <= dateStr && slot.dateTo >= dateStr;
}

function collectRecurringWorkLocationEntries(slots: CalendarSlot[], dateStr: string): LocationEntry[] {
  const entries: LocationEntry[] = [];

  for (const slot of slots) {
    if (!slot.recurrenceRule || !isWorkLocationSlotData(slot.data)) continue;

    const occurrences = expandRecurrence(
      { dateFrom: slot.dateFrom, dateTo: slot.dateTo, recurrenceRule: slot.recurrenceRule },
      dateStr,
      dateStr
    );
    if (occurrences.length === 0) continue;

    entries.push(...slot.data.entries);
  }

  return entries;
}

export function resolveEffectiveLocationEntries(
  calendarData: unknown,
  slots: CalendarSlot[],
  dateStr: string,
  personLocations: readonly PersonLocationAssignment[] = []
): LocationEntry[] {
  // Step 1: Check for location-override slots covering this date.
  const overrideEntries: LocationEntry[] = [];
  for (const slot of slots) {
    if (!slotCoversDate(slot, dateStr)) continue;
    if (!isLocationOverrideSlotData(slot.data)) continue;

    overrideEntries.push(...slot.data.entries);
  }
  if (overrideEntries.length > 0) {
    return overrideEntries;
  }

  // Step 2: Check recurring work-location slots.
  const recurringEntries = collectRecurringWorkLocationEntries(slots, dateStr);
  if (recurringEntries.length > 0) {
    return recurringEntries;
  }

  // Step 3: Fall back to legacy weekly location pattern.
  const pattern = resolveWeeklyLocationPattern(calendarData, personLocations);
  if (!pattern) return [];

  const weekday = isoWeekdayFromDateKey(dateStr);
  if (weekday === null) return [];

  return pattern[weekday as IsoWeekday] ?? [];
}

/**
 * Resolve the effective location for a date (and optionally a time).
 *
 * Precedence: override slot > recurring work-location slot > weekly pattern (deprecated) > null.
 *
 * @param calendarData - The calendar's `data` JSONB (may contain legacy weeklyLocationPattern).
 * @param slots        - All calendar slots (overrides, recurring work-location, etc.).
 * @param dateStr      - Date in YYYY-MM-DD format.
 * @param timeStr      - Optional time in HH:mm format. When omitted, only full-day entries match.
 * @param personLocations - Optional person-location assignments used to derive a default primary-location pattern.
 * @returns The locationId of the matching entry, or null if the employee is not bookable.
 */
export function resolveEffectiveLocation(
  calendarData: unknown,
  slots: CalendarSlot[],
  dateStr: string,
  timeStr?: string,
  personLocations: readonly PersonLocationAssignment[] = []
): string | null {
  const entries = resolveEffectiveLocationEntries(calendarData, slots, dateStr, personLocations);
  if (entries.length === 0) return null;
  const match = findMatchingEntry(entries, timeStr);
  return match?.locationId ?? null;
}

/**
 * Check whether any two entries in a list have overlapping time ranges.
 *
 * Returns the indices of the first pair that overlaps, or null if no overlap.
 */
export function findOverlappingEntries(entries: LocationEntry[]): [number, number] | null {
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const aRange = resolveEntryMinutes(entries[i]);
      const bRange = resolveEntryMinutes(entries[j]);

      if (!aRange || !bRange) return [i, j];

      // Invalid range (end <= start) is treated as an overlap to flag the error
      if (aRange.end <= aRange.start || bRange.end <= bRange.start) return [i, j];

      // Ranges overlap if one starts before the other ends
      if (aRange.start < bRange.end && bRange.start < aRange.end) return [i, j];
    }
  }

  return null;
}

/**
 * Validate that endTime is after startTime.
 */
export function isValidTimeRange(startTime: string, endTime: string): boolean {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start === null || end === null) return true;
  return end > start;
}
